import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';
import './Login.css';

const RECAPTCHA_SITE_KEY = '6Le1MmcsAAAAALQURwNWLSVXb-SvYUjA0MWtNXD7';

export const Login = () => {
  const { i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const isGerman = i18n.language === 'de';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      alert(isGerman ? 'Bitte bestätigen Sie das reCAPTCHA' : 'Please complete the reCAPTCHA');
      return;
    }

    setLoading(true);

    try {
      // Add recaptcha token to login data
      const loginData = {
        email: formData.email,
        password: formData.password,
        recaptcha_token: recaptchaToken
      };
      
      await login(loginData.email, loginData.password, loginData.recaptcha_token);
      toast.success(isGerman ? 'Erfolgreich angemeldet' : 'Successfully logged in');
      navigate('/profile');
    } catch (error) {
      console.error('Login error:', error);
      let message = isGerman ? 'Ungültige E-Mail oder Passwort' : 'Invalid email or password';
      
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      
      alert(message);
      toast.error(message, { duration: 5000 });
      recaptchaRef.current?.reset();
      setRecaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">{isGerman ? 'Anmelden' : 'Login'}</h1>
          <p className="login-subtitle">{isGerman ? 'Melden Sie sich bei Ihrem Konto an' : 'Sign in to your account'}</p>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">{isGerman ? 'E-Mail-Adresse' : 'Email Address'}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{isGerman ? 'Passwort' : 'Password'}</label>
              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  style={{ paddingRight: '3.5rem' }}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.75rem',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    width: '2.5rem',
                    height: '2.5rem',
                    zIndex: 10,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* reCAPTCHA */}
            <div className="recaptcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                data-testid="login-recaptcha"
              />
            </div>

            <div className="form-actions">
              <Link to="/forgot-password" className="forgot-link">
                {isGerman ? 'Passwort vergessen?' : 'Forgot password?'}
              </Link>
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !recaptchaToken}
              data-testid="login-submit-button"
            >
              {loading ? (isGerman ? 'Lädt...' : 'Loading...') : (isGerman ? 'Anmelden' : 'Login')}
            </button>
          </form>

          <p className="register-link">
            {isGerman ? 'Noch kein Konto?' : "Don't have an account?"}{' '}
            <Link to="/register">{isGerman ? 'Hier registrieren' : 'Register here'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
